import { useMemo } from 'react';
import { alpha, Box, Typography, useTheme } from '@mui/material';
import { useRecoilValue } from 'recoil';
import { negativeTrendColorState, positiveTrendColorState } from 'states';
import { useOptionsCalculatorComputed } from 'hooks/optionsCalculator/useOptionsCalculatorComputed';
import { useOptionsCalculatorData } from 'hooks/optionsCalculator/useOptionsCalculatorData';
import { EquityFieldKey } from 'types';
import type { DividendRecord } from 'types/hiro';
import { dayjs, ET, etCalendarDaysUntil } from 'util/shared';
import {
  HEATMAP_METRICS,
  HeatmapMetric,
  toDollarValue,
} from './heatmapMetrics';

interface PnlHeatmapProps {
  isFullscreen?: boolean;
  numDateCols?: number;
  numPriceRows?: number;
  enabledMetrics?: HeatmapMetric[];
  rangeFactor?: number;
  dividends?: DividendRecord[];
}

const formatDollar = (value: number): string => {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) {
    return `${sign}$${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  }
  if (abs >= 1_000) {
    return `${sign}$${(abs / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  }
  return `${sign}$${Math.round(abs)}`;
};

const getPriceFractionDigits = (prices: number[]): number => {
  const positiveSteps = prices
    .slice(1)
    .map((price, index) => Math.abs(price - prices[index]))
    .filter((step) => Number.isFinite(step) && step > Number.EPSILON);
  if (positiveSteps.length === 0) {
    return 2;
  }

  // At least 2 so half-dollar strikes (402.5) aren't rounded.
  const step = Math.min(...positiveSteps);
  return step < 1 ? Math.min(6, Math.max(2, Math.ceil(-Math.log10(step)))) : 2;
};

const formatPrice = (price: number, fractionDigits: number): string =>
  price.toLocaleString('en-US', {
    maximumFractionDigits: fractionDigits,
  });

const PnlHeatmap = ({
  isFullscreen = false,
  numDateCols = 12,
  numPriceRows = 21,
  enabledMetrics = ['pnl'],
  rangeFactor = 1,
  dividends = [],
}: PnlHeatmapProps) => {
  const theme = useTheme();
  const posColor = useRecoilValue(positiveTrendColorState);
  const negColor = useRecoilValue(negativeTrendColorState);
  const { getPnlHeatmap } = useOptionsCalculatorComputed();
  const { currentPrice, currentEquity } = useOptionsCalculatorData();

  const heatmap = useMemo(
    () => getPnlHeatmap(numDateCols, numPriceRows, rangeFactor),
    [getPnlHeatmap, numDateCols, numPriceRows, rangeFactor],
  );

  // Dollar-value grid + max magnitude (for color scaling), computed per
  // metric so any combination of toggles can be shown side by side.
  const metricGrids = useMemo(() => {
    const result = {} as Record<
      HeatmapMetric,
      { grid: (number | null)[][]; maxAbs: number }
    >;
    if (heatmap == null) {
      return result;
    }
    HEATMAP_METRICS.forEach(({ value: m }) => {
      let maxAbs = 0;
      const grid = heatmap.cells.map((row, r) =>
        row.map((cell) => {
          if (cell == null) {
            return null;
          }
          const v = toDollarValue(cell, heatmap.prices[r], m);
          if (Number.isFinite(v)) {
            maxAbs = Math.max(maxAbs, Math.abs(v));
            return v;
          }
          return null;
        }),
      );
      result[m] = { grid, maxAbs };
    });
    return result;
  }, [heatmap]);

  const currentPriceRow = useMemo(() => {
    if (heatmap == null || currentPrice == null) {
      return -1;
    }
    let bestIdx = -1;
    let bestDiff = Infinity;
    heatmap.prices.forEach((p, i) => {
      const diff = Math.abs(p - currentPrice);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIdx = i;
      }
    });
    return bestIdx;
  }, [heatmap, currentPrice]);

  const priceFractionDigits = useMemo(
    () => getPriceFractionDigits(heatmap?.prices ?? []),
    [heatmap],
  );

  if (heatmap == null || heatmap.cells.length === 0) {
    return (
      <Box
        sx={{
          height: isFullscreen ? '92%' : 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2">
          Add a position to see the heatmap.
        </Typography>
      </Box>
    );
  }

  const { dates, prices } = heatmap;

  const earningsColor = theme.palette.warning.main;
  const dividendColor =
    theme.palette.info?.main ?? theme.palette.secondary.main;
  const eventAccent = (kind: 'earnings' | 'dividend'): string =>
    kind === 'earnings' ? earningsColor : dividendColor;

  // Map earnings / ex-dividend dates onto their heatmap column (exact calendar
  // day when present, else nearest column for the even-spaced fallback).
  type HeatmapEvent = {
    kind: 'earnings' | 'dividend';
    label: string;
  };
  const eventsByCol = new Map<number, HeatmapEvent[]>();
  const markEvent = (
    ts: number,
    kind: 'earnings' | 'dividend',
    label: string,
  ): void => {
    if (!Number.isFinite(ts)) {
      return;
    }
    const eventDay = dayjs(ts).tz(ET).startOf('day');
    const firstDay = dayjs(dates[0]).tz(ET).startOf('day');
    const lastDay = dayjs(dates[dates.length - 1])
      .tz(ET)
      .startOf('day');
    const isBeforeHeatmap =
      kind === 'earnings' ? ts < dates[0] : eventDay.isBefore(firstDay);
    const isAfterHeatmap =
      kind === 'earnings'
        ? ts > dates[dates.length - 1]
        : eventDay.isAfter(lastDay);
    if (isBeforeHeatmap || isAfterHeatmap) {
      return;
    }

    const dayStr = eventDay.format('YYYY-MM-DD');
    let col = dates.findIndex(
      (d) => dayjs(d).tz(ET).format('YYYY-MM-DD') === dayStr,
    );
    if (col === -1) {
      let best = Infinity;
      dates.forEach((d, i) => {
        const diff = Math.abs(d - ts);
        if (diff < best) {
          best = diff;
          col = i;
        }
      });
    }
    if (col >= 0) {
      const existingEvents = eventsByCol.get(col) ?? [];
      if (!existingEvents.some((event) => event.kind === kind)) {
        eventsByCol.set(col, [...existingEvents, { kind, label }]);
      }
    }
  };
  const earningsRaw = currentEquity?.[EquityFieldKey.earningsDate];
  if (earningsRaw != null && earningsRaw !== '') {
    markEvent(dayjs.utc(earningsRaw).valueOf(), 'earnings', 'ER');
  }
  dividends.forEach((d) => {
    const amount = d.dividend ?? d.adj_dividend;
    if (d.date != null && d.date !== '' && amount != null && amount > 0) {
      markEvent(
        dayjs.tz(d.date, ET).startOf('day').valueOf(),
        'dividend',
        'Div',
      );
    }
  });

  const columnLabel = (ts: number, index: number): string => {
    if (dates.length === 1 || index === 0) {
      return 'Now';
    }
    if (index === dates.length - 1) {
      return 'Exp';
    }
    return dayjs(ts).tz(ET).format('MMM D');
  };

  const dteLabel = (ts: number): string => `${etCalendarDaysUntil(dayjs(ts))}d`;

  const renderMetricSection = (m: HeatmapMetric) => {
    const { grid, maxAbs } = metricGrids[m] ?? { grid: [], maxAbs: 0 };
    const metricLabel =
      HEATMAP_METRICS.find((hm) => hm.value === m)?.label ?? m;
    const legendLabels =
      m === 'pnl' ? ['Loss', 'Profit'] : ['Negative', 'Positive'];

    const cellColor = (value: number | null): string => {
      if (value == null || maxAbs === 0) {
        return 'transparent';
      }
      const base = value >= 0 ? posColor : negColor;
      const intensity = Math.min(1, Math.abs(value) / maxAbs);
      return alpha(base, 0.1 + 0.62 * intensity);
    };

    return (
      <Box key={m} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 600,
            color: 'text.secondary',
            mb: 0.5,
          }}
        >
          {metricLabel}
        </Typography>
        <Box
          role="table"
          aria-label={`${metricLabel} across price and date`}
          sx={{
            display: 'grid',
            gridTemplateColumns: `72px repeat(${dates.length}, minmax(46px, 1fr))`,
            gap: '2px',
            minWidth: 'fit-content',
          }}
        >
          <Box
            sx={{
              position: 'sticky',
              left: 0,
              zIndex: 2,
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-start',
              px: 0.5,
              pb: 0.5,
            }}
          >
            <Typography sx={{ fontSize: '10px', color: 'text.tertiary' }}>
              Price
            </Typography>
          </Box>
          {dates.map((ts, c) => {
            const events = eventsByCol.get(c) ?? [];
            const accent =
              events.length > 0 ? eventAccent(events[0].kind) : undefined;
            return (
              <Box
                key={`h-${m}-${ts}-${c}`}
                sx={{
                  textAlign: 'center',
                  pb: 0.5,
                  lineHeight: 1.1,
                  display: 'flex',
                  flexDirection: 'column',
                  borderBottom:
                    events.length > 0
                      ? `2px solid ${accent}`
                      : '2px solid transparent',
                }}
              >
                {events.length > 0 && (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      gap: 0.5,
                    }}
                  >
                    {events.map((event) => (
                      <Typography
                        key={event.kind}
                        sx={{
                          fontSize: '8px',
                          fontWeight: 700,
                          color: eventAccent(event.kind),
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                          lineHeight: 1.1,
                        }}
                      >
                        {event.label}
                      </Typography>
                    ))}
                  </Box>
                )}
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: events.length > 0 ? 700 : 500,
                    color:
                      events.length > 0 ? 'text.primary' : 'text.secondary',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {columnLabel(ts, c)}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '9px',
                    color: 'text.tertiary',
                  }}
                >
                  {dteLabel(ts)}
                </Typography>
              </Box>
            );
          })}

          {prices.map((price, r) => {
            const isCurrent = r === currentPriceRow;
            return [
              <Box
                key={`p-${m}-${r}`}
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                  bgcolor: 'background.paper',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  pr: 0.75,
                  borderRight: isCurrent
                    ? `2px solid ${theme.palette.text.primary}`
                    : '2px solid transparent',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '10px',
                    fontWeight: isCurrent ? 600 : 400,
                    color: isCurrent ? 'text.primary' : 'text.secondary',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatPrice(price, priceFractionDigits)}
                </Typography>
              </Box>,
              ...(grid[r] ?? []).map((value, c) => {
                const events = eventsByCol.get(c) ?? [];
                const leftEventBorder =
                  events.length > 0
                    ? `2px solid ${alpha(eventAccent(events[0].kind), 0.55)}`
                    : undefined;
                const rightEventBorder =
                  events.length > 0
                    ? `2px solid ${alpha(
                        eventAccent(events[events.length - 1].kind),
                        0.55,
                      )}`
                    : undefined;
                return (
                  <Box
                    key={`c-${m}-${r}-${c}`}
                    title={
                      value == null
                        ? undefined
                        : `${formatPrice(
                            price,
                            priceFractionDigits,
                          )} on ${columnLabel(
                            dates[c],
                            c,
                          )}: ${formatDollar(value)}`
                    }
                    sx={{
                      minHeight: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '2px',
                      backgroundColor: cellColor(value),
                      borderTop: isCurrent
                        ? `1px solid ${alpha(theme.palette.text.primary, 0.55)}`
                        : '1px solid transparent',
                      borderBottom: isCurrent
                        ? `1px solid ${alpha(theme.palette.text.primary, 0.55)}`
                        : '1px solid transparent',
                      borderLeft: leftEventBorder,
                      borderRight: rightEventBorder,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 500,
                        color: 'text.primary',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {value == null ? '' : formatDollar(value)}
                    </Typography>
                  </Box>
                );
              }),
            ];
          })}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            pt: 1,
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
            {legendLabels[0]}
          </Typography>
          <Box
            sx={{
              width: 160,
              height: 8,
              borderRadius: '4px',
              background: `linear-gradient(90deg, ${alpha(
                negColor,
                0.72,
              )} 0%, ${alpha(negColor, 0.1)} 45%, ${alpha(
                posColor,
                0.1,
              )} 55%, ${alpha(posColor, 0.72)} 100%)`,
            }}
          />
          <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
            {legendLabels[1]}
          </Typography>
          {eventsByCol.size > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginLeft: '16px',
              }}
            >
              {(['earnings', 'dividend'] as const)
                .filter((kind) =>
                  [...eventsByCol.values()]
                    .flat()
                    .some((event) => event.kind === kind),
                )
                .map((kind) => (
                  <Box
                    key={kind}
                    sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: '2px',
                        backgroundColor: eventAccent(kind),
                      }}
                    />
                    <Typography
                      sx={{ fontSize: '10px', color: 'text.secondary' }}
                    >
                      {kind === 'earnings' ? 'Earnings' : 'Ex-dividend'}
                    </Typography>
                  </Box>
                ))}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: isFullscreen ? '92%' : 'auto',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ overflowX: 'auto' }}>
        {enabledMetrics.length === 0 ? (
          <Box
            sx={{
              minHeight: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2">
              Enable a metric above to see its heatmap.
            </Typography>
          </Box>
        ) : (
          HEATMAP_METRICS.filter(({ value }) =>
            enabledMetrics.includes(value),
          ).map(({ value }) => renderMetricSection(value))
        )}
      </Box>
    </Box>
  );
};

export default PnlHeatmap;
